import { inject, Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, ParamMap, Params, Router, UrlTree } from '@angular/router';

import { Logger } from '../../services/logger/logger.service';
import { ConversationEntityTypeEnum } from 'src/app/@shared/enums/conversation-entity-type.enum';
import { NotificationTypeEnum } from 'src/app/@shared/enums/notification-type.enum';

const LOGGER = new Logger('NotificationRouteGuard');

/**
 * Traduit la charge d'une notification push en écran de l'application (#173).
 *
 * Le serveur envoie un **type d'évènement et des identifiants d'objets** — il ne
 * connaît pas, et ne doit pas connaître, notre routage. Le service worker se
 * contente de recopier cette charge en paramètres de requête sur
 * `/notifications/open`, sans rien en interpréter : ajouter un type d'évènement
 * ne demande donc de toucher ni au serveur, ni au service worker, seulement la
 * table ci-dessous.
 *
 * Le dossier vient de la charge (`client_id`) et **non** du dossier courant : la
 * notification peut concerner un autre dossier que celui qui est ouvert.
 */
@Injectable({
    providedIn: 'root'
})
export class NotificationRouteGuard {
    private _router = inject(Router);

    canActivate(route: ActivatedRouteSnapshot): UrlTree {
        const params = route.queryParamMap;
        const type = params.get('type');
        const clientId = Number(params.get('client_id'));

        // Sans dossier, on ne peut pas construire d'écran : on ouvre l'application,
        // qui rouvre le dernier dossier travaillé ou le sélecteur.
        if (!Number.isInteger(clientId) || clientId <= 0) {
            LOGGER.warn('Notification sans dossier exploitable, ouverture par défaut', type);
            return this._router.createUrlTree(['/']);
        }

        const target = this._targetFor(type, params);
        LOGGER.debug('Notification ouverte sur', type, target.path);
        return this._router.createUrlTree(
            [`/c/${clientId}${target.path}`],
            target.queryParams ? { queryParams: target.queryParams } : {}
        );
    }

    /*----------------------------------------------------------------------------------------
     * Méthodes privées
     ----------------------------------------------------------------------------------------*/

    /**
     * Écran d'un type d'évènement, relatif au dossier. Un type inconnu — notamment
     * un type ajouté côté serveur avant d'être câblé ici — ouvre le tableau de bord
     * du bon dossier plutôt que de laisser l'utilisateur nulle part.
     */
    private _targetFor(type: string | null, params: ParamMap): { path: string; queryParams?: Params } {
        switch (type) {
            case NotificationTypeEnum.BridgeItemDisconnected:
            case NotificationTypeEnum.BridgeItemAuthExpiring:
            case NotificationTypeEnum.BankAccountSyncRequest:
                return { path: '/settings/banking/bank-accounts' };
            case NotificationTypeEnum.BankCsvImport:
                return { path: '/banking/bank-account-lines' };
            case NotificationTypeEnum.ConversationNewMessage:
                return this._conversationPath(params);
            default:
                LOGGER.warn('Type de notification non câblé, repli sur le tableau de bord', type);
                return { path: '/dashboard' };
        }
    }

    /**
     * Une conversation n'a pas d'écran propre : le fil s'affiche sur l'objet qui le
     * porte. On ouvre donc cet objet, désigné par `entity_type` et `object_id`.
     *
     * ⚠️ Ces deux champs sont **optionnels** côté serveur : absents si la discussion
     * n'est rattachée à aucun objet métier. Faute d'écran de conversation autonome,
     * on ouvre alors le tableau de bord du bon dossier.
     *
     * Les objets se répartissent en deux familles, et **les deux ouvrent l'objet
     * lui-même** depuis #995 : les trois documents de vente et d'achat ont une page à
     * leur adresse propre ; les autres s'affichent dans une fenêtre par-dessus leur
     * liste, désignée par `?selected=`. Le fil visé est passé en `?conversation=`.
     */
    private _conversationPath(params: ParamMap): { path: string; queryParams?: Params } {
        const objectId = Number(params.get('object_id'));
        const entityType = params.get('entity_type');

        if (!entityType || !Number.isInteger(objectId) || objectId <= 0) {
            LOGGER.debug('Discussion rattachée à aucun objet, ouverture du tableau de bord');
            return { path: '/dashboard' };
        }

        const conversationParams = this._conversationQueryParams(params);

        const listPath = this._listPathFor(entityType);
        if (listPath) {
            return { path: listPath, queryParams: { selected: objectId, ...conversationParams } };
        }

        switch (entityType) {
            case ConversationEntityTypeEnum.PurchaseInvoice:
                return { path: `/purchasing/invoice/${objectId}`, queryParams: conversationParams };
            case ConversationEntityTypeEnum.Quotation:
                return { path: `/sales/quotation/${objectId}`, queryParams: conversationParams };
            case ConversationEntityTypeEnum.SalesInvoice:
                return { path: `/sales/invoice/${objectId}`, queryParams: conversationParams };
            default:
                LOGGER.warn('Objet porteur de conversation non câblé', entityType);
                return { path: '/dashboard' };
        }
    }

    /**
     * Liste sur laquelle s'ouvre la fenêtre de détail d'un objet, ou `null` quand
     * l'objet a une page à lui.
     */
    private _listPathFor(entityType: string): string | null {
        switch (entityType) {
            case ConversationEntityTypeEnum.BankAccountLine:
                return '/banking/bank-account-lines';
            case ConversationEntityTypeEnum.CardReceipt:
                return '/banking/card-receipts';
            case ConversationEntityTypeEnum.CashTransaction:
                return '/banking/cash-transactions';
            case ConversationEntityTypeEnum.Cheque:
                return '/banking/cheques';
            case ConversationEntityTypeEnum.CreditCardTransaction:
                return '/banking/credit-card-transactions';
            case ConversationEntityTypeEnum.Deposit:
                return '/banking/deposits';
            case ConversationEntityTypeEnum.Employee:
                return '/human-resources/employees';
            case ConversationEntityTypeEnum.Shareholder:
                return '/client/shareholders';
            default:
                return null;
        }
    }

    /**
     * Fil à afficher, quand la charge le porte. Vaut pour les deux familles d'objets : sur une
     * liste comme sur une page, c'est `ConversationIndicatorComponent` qui lit ce paramètre.
     *
     * Le fil n'est ajouté que si la charge le nomme : un `conversation` vide ferait chercher
     * un fil inexistant, là où son absence laisse le volet fermé.
     */
    private _conversationQueryParams(params: ParamMap): Params {
        const conversationId = Number(params.get('conversation_id'));

        if (!Number.isInteger(conversationId) || conversationId <= 0) {
            return {};
        }

        return { conversation: conversationId };
    }
}
